import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SortingState } from '@tanstack/react-table';
import { SearchParam, SortingType, TableType } from '@common/ts/enums';
import { TranscriptsView } from '@/enums';
import {
  getPaginationParamPage,
  getSortingParamBy,
  getSortingParamType,
  getFilteringParamName,
} from '@/utils/urlParams';
import { UseUrlTableProps, UrlTableProps, MultiViewUrlTableProps } from '@/types';

// Helper function to update URL parameters
const updateUrlParams = (
  searchParams: URLSearchParams,
  setSearchParams: (params: URLSearchParams) => void,
  updates: Array<{ key: string; value: string | null; deleteIfDefault?: string }>,
) => {
  const newSearchParams = new URLSearchParams(searchParams);
  updates.forEach(({ key, value, deleteIfDefault }) => {
    if (value === null || value === deleteIfDefault) {
      newSearchParams.delete(key);
    } else {
      newSearchParams.set(key, value);
    }
  });

  setSearchParams(newSearchParams);
};

// Helper function to create state sync logic
const useUrlState = <T>(
  getFromUrl: () => T,
  dependencies: React.DependencyList,
): [T, React.Dispatch<React.SetStateAction<T>>] => {
  const [state, setState] = useState<T>(getFromUrl);
  useEffect(() => {
    const urlValue = getFromUrl();
    setState((prev) => (structuredClone(urlValue) !== structuredClone(prev) ? urlValue : prev));
  }, dependencies);

  return [state, setState];
};

export const useUrlTable = ({
  type,
  activeView,
}: UseUrlTableProps): UrlTableProps | MultiViewUrlTableProps => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Base parameter names
  const basePaginationParam = getPaginationParamPage(type);
  const sortByParam = getSortingParamBy(type);
  const sortTypeParam = getSortingParamType(type);

  // For single-view tables (Users, Projects, etc.)
  const useSingleViewPagination = () => {
    const getPageFromUrl = useCallback(
      () => +searchParams.get(basePaginationParam) || 1,
      [searchParams, basePaginationParam],
    );

    const [urlCurrentPage, setUrlCurrentPage] = useUrlState(getPageFromUrl, [
      searchParams,
      basePaginationParam,
    ]);

    const updateUrlPage = useCallback(
      (page: number) => {
        setUrlCurrentPage(page);
        updateUrlParams(searchParams, setSearchParams, [
          { key: basePaginationParam, value: page.toString(), deleteIfDefault: '1' },
        ]);
      },
      [searchParams, setSearchParams, basePaginationParam, setUrlCurrentPage],
    );

    return { urlCurrentPage, updateUrlPage };
  };

  // For multi-view tables (Transcripts)
  const useMultiViewPagination = () => {
    const completedPageParam = getPaginationParamPage(TableType.TRANSCRIPTS);
    const incompletePageParam = getPaginationParamPage(TableType.INCOMPLETE_TRANSCRIPTS);
    const screenedOutPageParam = getPaginationParamPage(TableType.SCREENED_OUT_TRANSCRIPTS);
    const translatedPageParam = getPaginationParamPage(TableType.TRANSLATED_TRANSCRIPTS);

    const getCompletedPageFromUrl = useCallback(
      () => +searchParams.get(completedPageParam) || 1,
      [searchParams, completedPageParam],
    );

    const getIncompletePageFromUrl = useCallback(
      () => +searchParams.get(incompletePageParam) || 1,
      [searchParams, incompletePageParam],
    );

    const getScreenedOutPageFromUrl = useCallback(
      () => +searchParams.get(screenedOutPageParam) || 1,
      [searchParams, screenedOutPageParam],
    );

    const getTranslatedPageFromUrl = useCallback(
      () => +searchParams.get(translatedPageParam) || 1,
      [searchParams, translatedPageParam],
    );

    const [completedPage, setCompletedPage] = useUrlState(getCompletedPageFromUrl, [
      searchParams,
      completedPageParam,
    ]);
    const [incompletePage, setIncompletePage] = useUrlState(getIncompletePageFromUrl, [
      searchParams,
      incompletePageParam,
    ]);
    const [screenedOutPage, setScreenedOutPage] = useUrlState(getScreenedOutPageFromUrl, [
      searchParams,
      screenedOutPageParam,
    ]);
    const [translatedPage, setTranslatedPage] = useUrlState(getTranslatedPageFromUrl, [
      searchParams,
      translatedPageParam,
    ]);

    const updateCompletedPage = useCallback(
      (page: number) => {
        setCompletedPage(page);
        updateUrlParams(searchParams, setSearchParams, [
          { key: completedPageParam, value: page.toString(), deleteIfDefault: '1' },
        ]);
      },
      [searchParams, setSearchParams, completedPageParam, setCompletedPage],
    );

    const updateIncompletePage = useCallback(
      (page: number) => {
        setIncompletePage(page);
        updateUrlParams(searchParams, setSearchParams, [
          { key: incompletePageParam, value: page.toString(), deleteIfDefault: '1' },
        ]);
      },
      [searchParams, setSearchParams, incompletePageParam, setIncompletePage],
    );

    const updateScreenedOutPage = useCallback(
      (page: number) => {
        setScreenedOutPage(page);
        updateUrlParams(searchParams, setSearchParams, [
          { key: screenedOutPageParam, value: page.toString(), deleteIfDefault: '1' },
        ]);
      },
      [searchParams, setSearchParams, screenedOutPageParam, setScreenedOutPage],
    );

    const updateTranslatedPage = useCallback(
      (page: number) => {
        setTranslatedPage(page);
        updateUrlParams(searchParams, setSearchParams, [
          { key: translatedPageParam, value: page.toString(), deleteIfDefault: '1' },
        ]);
      },
      [searchParams, setSearchParams, translatedPageParam, setTranslatedPage],
    );

    // Current page and updater based on context (language or active view)
    const translationLanguage = searchParams.get(SearchParam.TRANSLATION_LANGUAGE);

    // When in translation mode, use translated pagination regardless of view
    if (translationLanguage) {
      return {
        urlCurrentPage: translatedPage,
        updateUrlPage: updateTranslatedPage,
        completedPage,
        incompletePage,
        screenedOutPage,
        translatedPage,
      };
    }

    // Standard view-based pagination for non-translated content
    const currentPage =
      activeView === TranscriptsView.COMPLETED
        ? completedPage
        : activeView === TranscriptsView.SCREENED_OUT
          ? screenedOutPage
          : incompletePage;
    const updateCurrentPage =
      activeView === TranscriptsView.COMPLETED
        ? updateCompletedPage
        : activeView === TranscriptsView.SCREENED_OUT
          ? updateScreenedOutPage
          : updateIncompletePage;

    return {
      urlCurrentPage: currentPage,
      updateUrlPage: updateCurrentPage,
      completedPage,
      incompletePage,
      screenedOutPage,
      translatedPage,
    };
  };

  // Always call both hooks to maintain hook order consistency
  const singleViewResult = useSingleViewPagination();
  const multiViewResult = useMultiViewPagination();

  // Choose pagination strategy based on whether activeView is provided
  const pagination = activeView ? multiViewResult : singleViewResult;

  // Sorting
  const getSortingFromUrl = useCallback((): SortingState => {
    const sortBy = searchParams.get(sortByParam);
    const sortType = searchParams.get(sortTypeParam);

    return sortBy && sortType ? [{ id: sortBy, desc: sortType === SortingType.DESC }] : [];
  }, [searchParams, sortByParam, sortTypeParam]);

  const [urlSorting, setUrlSorting] = useUrlState(getSortingFromUrl, [getSortingFromUrl]);

  const updateUrlSorting = useCallback(
    (newSorting: SortingState | ((prev: SortingState) => SortingState)) => {
      const resolvedSorting =
        typeof newSorting === 'function' ? newSorting(urlSorting) : newSorting;
      setUrlSorting(resolvedSorting);

      if (resolvedSorting.length === 0) {
        updateUrlParams(searchParams, setSearchParams, [
          { key: sortByParam, value: null },
          { key: sortTypeParam, value: null },
        ]);
      } else {
        const { id, desc } = resolvedSorting[0];
        updateUrlParams(searchParams, setSearchParams, [
          { key: sortByParam, value: id },
          { key: sortTypeParam, value: desc ? SortingType.DESC : SortingType.ASC },
        ]);
      }
    },
    [urlSorting, searchParams, setSearchParams, sortByParam, sortTypeParam],
  );

  // Filtering
  const updateUrlFilter = useCallback(
    (columnName: string, value: string) => {
      const paramName = getFilteringParamName(type, columnName);
      const isEmpty = !value || value.trim() === '';

      updateUrlParams(searchParams, setSearchParams, [
        { key: paramName, value: isEmpty ? null : value },
      ]);
    },
    [searchParams, setSearchParams, type],
  );

  const getUrlFilterValue = useCallback(
    (columnName: string): string => {
      const paramName = getFilteringParamName(type, columnName);
      return searchParams.get(paramName) || '';
    },
    [searchParams, type],
  );

  return {
    ...pagination,
    urlSorting,
    updateUrlSorting,
    updateUrlFilter,
    getUrlFilterValue,
  };
};
